import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Search } from 'lucide-react';
import { CaseCptLine } from '@/lib/templates/types';
import { BASE_SURGEON_MODIFIERS, MODIFIER_DESCRIPTIONS } from '@/lib/modifiers/types';

interface AddModifierDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  cptLines: CaseCptLine[];
  onToggleModifier: (code: string, modifier: string) => void;
  onModifierAdded?: (line: CaseCptLine, modifier: string) => void;
}

const cptCode = (line: Pick<CaseCptLine, 'code'>) => String(line.code);

export function AddModifierDialog({
  open,
  onOpenChange,
  cptLines,
  onToggleModifier,
  onModifierAdded,
}: AddModifierDialogProps) {
  const [query, setQuery] = useState('');
  const [selectedModifier, setSelectedModifier] = useState<string | null>(null);
  const [selectedCode, setSelectedCode] = useState<string | null>(null);

  const filteredModifiers = BASE_SURGEON_MODIFIERS.filter(m => {
    if (!query) return true;
    const q = query.toLowerCase();
    return m.toLowerCase().includes(q) || (MODIFIER_DESCRIPTIONS[m] || '').toLowerCase().includes(q);
  });

  const handleAdd = () => {
    if (!selectedModifier || !selectedCode) return;
    const line = cptLines.find(l => cptCode(l) === selectedCode);
    if (!line) return;

    // Only add if not already present
    if (!line.modifiers.includes(selectedModifier)) {
      onToggleModifier(selectedCode, selectedModifier);
    }
    onModifierAdded?.(line, selectedModifier);

    setQuery('');
    setSelectedModifier(null);
    setSelectedCode(null);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Add Modifier</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Step 1: Select CPT */}
          <div className="space-y-1.5">
            <Label className="text-xs font-medium">Select Procedure:</Label>
            <RadioGroup value={selectedCode || ''} onValueChange={setSelectedCode}>
              {cptLines.map(l => (
                <div key={cptCode(l)} className="flex items-center space-x-2">
                  <RadioGroupItem value={cptCode(l)} id={`cpt-${cptCode(l)}`} />
                  <Label htmlFor={`cpt-${cptCode(l)}`} className="text-sm">
                    <span className="font-mono font-medium">{cptCode(l)}</span>
                    <span className="text-muted-foreground ml-1.5">{l.description}</span>
                  </Label>
                </div>
              ))}
            </RadioGroup>
          </div>

          {/* Step 2: Search modifier */}
          {selectedCode && (
            <>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search modifier code or description…"
                  value={query}
                  onChange={e => setQuery(e.target.value)}
                  className="pl-10"
                />
              </div>

              <div className="max-h-48 overflow-y-auto space-y-1">
                {filteredModifiers.map(m => {
                  const line = cptLines.find(l => cptCode(l) === selectedCode);
                  const alreadyApplied = line?.modifiers.includes(m);
                  return (
                    <div
                      key={m}
                      className={`p-2 rounded-md cursor-pointer text-sm flex items-center justify-between ${
                        selectedModifier === m
                          ? 'bg-primary/10 border border-primary/30'
                          : alreadyApplied
                            ? 'opacity-50 cursor-not-allowed'
                            : 'hover:bg-accent/50'
                      }`}
                      onClick={() => !alreadyApplied && setSelectedModifier(m)}
                    >
                      <div>
                        <span className="font-mono font-medium">{m}</span>
                        <span className="text-muted-foreground ml-2">{MODIFIER_DESCRIPTIONS[m] || ''}</span>
                      </div>
                      {alreadyApplied && (
                        <span className="text-[10px] text-muted-foreground">Applied</span>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleAdd} disabled={!selectedModifier || !selectedCode}>Add Modifier</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
